import { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { ExportButton } from '../../../utils/export';
import { ExportFormat } from '../../../utils/export/types';
import { SkeletonLoader } from './SkeletonLoader';
import { useIsMobile } from '../../../hooks/useIsMobile';

interface AnalyticsCardProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
  className?: string;
  headerActions?: ReactNode;
  isLoading?: boolean;
  error?: Error | null;
  emptyMessage?: string;
  exportData?: unknown[];
  exportFileName?: string;
  accent?: string;
  onBodyClick?: () => void;
}

export function AnalyticsCard({
  title,
  subtitle,
  children,
  className = '',
  headerActions,
  isLoading = false,
  error,
  emptyMessage = 'No data available',
  exportData,
  exportFileName,
  accent,
  onBodyClick,
}: AnalyticsCardProps) {
  const isMobile = useIsMobile();
  const shouldIgnoreClick = (eventTarget: EventTarget | null) => {
    if (!(eventTarget instanceof HTMLElement)) return false;
    const interactive =
      eventTarget.closest('button, input, select, textarea, a, [role="button"]') ||
      !!eventTarget.getAttribute('contenteditable');
    return interactive;
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onBodyClick) return;
    if (shouldIgnoreClick(event.target)) return;
    onBodyClick();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onBodyClick) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onBodyClick();
    }
  };
  return (
    <div
      className={`relative rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] shadow-sm ${className}`}
    >
      <div className={`flex min-h-0 items-center justify-between rounded-t-lg bg-[var(--anl-surface)] ${isMobile ? 'p-3' : 'p-4'}`}>
        <div className="min-w-0 flex-1">
          <h3 className={`truncate font-semibold text-[var(--anl-text)] ${isMobile ? 'text-base' : 'text-lg'}`}>{title}</h3>
          {subtitle && (
            <p className="mt-1 truncate text-sm text-[var(--anl-text-muted)]">{subtitle}</p>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center space-x-2">
          {exportData && exportData.length > 0 && (
            <ExportButton
              data={exportData}
              config={{
                fileName: exportFileName || title.toLowerCase().replace(/\s+/g, '-'),
                date: true,
              }}
              format={ExportFormat.CSV}
              className="text-[var(--anl-text-muted)] hover:text-[var(--anl-text)]"
            />
          )}
          {headerActions}
        </div>
      </div>

      <div
        role={onBodyClick ? 'button' : undefined}
        tabIndex={onBodyClick ? 0 : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`rounded-b-lg bg-[var(--anl-surface)] ${isMobile ? 'p-3' : 'p-4'}`}
      >
        {isLoading ? (
          <SkeletonLoader variant="chart" height="h-64" />
        ) : error ? (
          <div className="py-8 text-center text-[var(--anl-text-muted)]">
            <p className="text-sm">Error loading data</p>
          </div>
        ) : children ? (
          children
        ) : (
          <div className="py-8 text-center text-[var(--anl-text-muted)]">
            <p className="text-sm">{emptyMessage}</p>
          </div>
        )}
      </div>

      {accent ? (
        <div
          className="absolute bottom-0 left-0 right-0 rounded-b-lg"
          style={{ height: 6, backgroundColor: accent }}
        />
      ) : null}
    </div>
  );
}
